import {
  Table,
  Thead,
  Td,
  Th,
  Tr,
  Tbody,
  Box,
  Flex,
  Skeleton,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  Row,
} from '@tanstack/react-table';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import {
  useGetUserActiveCreditFacilitiesQuery,
  CreditFacilityGQLType,
  CoreBorrowingBaseInsightsGqlDataGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  borrowingBaseInsightsColumnIds,
  checkmarkColumnHeaderIds,
  createInitialColumnVisibility,
  fixedColumnsStyleToColumnIndexMap,
  formatCellValue,
  getBorrowingBaseInsightsValueForColumn,
  portfolioOverviewFacilitiesTableColumns,
  portfolioOverviewFacilityKeys,
  PortfolioOverviewFacilityRow,
  stickyHeaderRowZIndex,
} from './PortfoliioOverviewFacilitiesTableUtils';
import { PortfolioOverviewFacilitiesColumnSelector } from './PortfolioOverviewFacilitiesTableComponents';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
} from 'packages/common-frontend/constants';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { useGetDailyCoreBorrowingBaseInsightsQuery } from 'packages/portfolio/requests/requests.gql.generated';

const getCreditFacilityIdForSlug = (
  creditFacilitySlug: string,
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'slug'>[],
) => {
  const creditFacilityId = _.find(
    creditFacilities,
    ({ slug }) => slug === creditFacilitySlug,
  )?.id;

  finleyAssert(
    creditFacilityId,
    `creditFacilityId must exist for [creditFacilityId: ${creditFacilityId}] for the user.`,
  );

  return creditFacilityId;
};

const StickyTd: React.FC<{
  children: React.ReactNode;
  cellIndex: number;
  onClick: () => void;
}> = ({ children, cellIndex, onClick }) => {
  return (
    <Td
      cursor="pointer"
      position="sticky"
      backgroundColor="white"
      _hover={{
        backgroundColor: colors.gray[50],
        textDecoration: 'underline',
      }}
      style={{
        ...fixedColumnsStyleToColumnIndexMap[cellIndex],
        zIndex: stickyHeaderRowZIndex,
      }}
      fontSize="sm"
      padding="16px"
      whiteSpace="nowrap"
      onClick={onClick}
    >
      {children}
    </Td>
  );
};

const NonStickyTd: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <Td
      cursor="default"
      zIndex={stickyHeaderRowZIndex}
      fontSize="sm"
      padding="16px"
      whiteSpace="nowrap"
    >
      {children}
    </Td>
  );
};

const PortfolioOverviewFacilitiesTableTd: React.FC<{
  children: React.ReactNode;
  cellIndex: number;
  onClick: () => void;
}> = ({ children, cellIndex, onClick }) => {
  return (
    <>
      {fixedColumnsStyleToColumnIndexMap[cellIndex] ? (
        <StickyTd cellIndex={cellIndex} onClick={onClick}>
          {children}
        </StickyTd>
      ) : (
        <NonStickyTd>{children}</NonStickyTd>
      )}
    </>
  );
};

const PortfolioOverviewFacilitiesTableRowSkeleton: React.FC<{
  row: Row<PortfolioOverviewFacilityRow>;
  creditFacilityId: string;
}> = ({ row, creditFacilityId }) => {
  const history = useHistory();
  const { setCreditFacilityId } = useCreditFacilityContext();

  const creditFacilityNameColumnId: typeof portfolioOverviewFacilityKeys[number] =
    'creditFacilityName';

  return (
    <Tr key={row.id}>
      {_.map(row.getVisibleCells(), (cell, index) => {
        const columnId = cell.column
          .id as typeof portfolioOverviewFacilityKeys[number];

        const isCreditFacilityNameColumn =
          columnId === creditFacilityNameColumnId;

        const formattedCellValue =
          isCreditFacilityNameColumn &&
          formatCellValue({
            cellValue: cell.getValue(),
            columnId,
          });

        return (
          <PortfolioOverviewFacilitiesTableTd
            key={`${row.id}-${index}`}
            cellIndex={index}
            onClick={() => {
              const portfolioSummaryRoute = `/portfolio/summary?${creditFacilityIdQueryParam}=${creditFacilityId}`;

              setCreditFacilityId(creditFacilityId);
              history.push(portfolioSummaryRoute);
            }}
          >
            {isCreditFacilityNameColumn && (
              <Box
                mr="8px"
                color={
                  fixedColumnsStyleToColumnIndexMap[index] && colors.blue[400]
                }
              >
                {flexRender(formattedCellValue, cell.getContext())}
              </Box>
            )}
            {!isCreditFacilityNameColumn && <Skeleton height="20px" />}
          </PortfolioOverviewFacilitiesTableTd>
        );
      })}
    </Tr>
  );
};

const PortfolioOverviewFacilitiesTableRow: React.FC<{
  row: Row<PortfolioOverviewFacilityRow>;
  creditFacilityId: string;
  latestBorrowingBaseInsights?: CoreBorrowingBaseInsightsGqlDataGQLType;
}> = ({ row, creditFacilityId, latestBorrowingBaseInsights }) => {
  const history = useHistory();
  const { setCreditFacilityId } = useCreditFacilityContext();

  return (
    <Tr key={row.id}>
      {_.map(row.getVisibleCells(), (cell, index) => {
        let cellValue = cell.getValue();
        const columnId = cell.column
          .id as typeof portfolioOverviewFacilityKeys[number];

        if (
          latestBorrowingBaseInsights &&
          _.includes(borrowingBaseInsightsColumnIds, columnId)
        ) {
          const cellValueFromInsights = getBorrowingBaseInsightsValueForColumn({
            columnId,
            insights: latestBorrowingBaseInsights,
          });
          if (cellValueFromInsights) {
            cellValue = cellValueFromInsights;
          }
        }

        const formattedCellValue = formatCellValue({
          cellValue,
          columnId,
        });

        const addIconToCell = _.includes(
          checkmarkColumnHeaderIds,
          cell.column.id,
        );

        return (
          <PortfolioOverviewFacilitiesTableTd
            key={`${row.id}-${index}`}
            cellIndex={index}
            onClick={() => {
              const portfolioSummaryRoute = `/portfolio/summary?${creditFacilityIdQueryParam}=${creditFacilityId}`;

              setCreditFacilityId(creditFacilityId);
              history.push(portfolioSummaryRoute);
            }}
          >
            <Flex alignItems="center">
              {/* if there are 0 failed covenants */}
              {addIconToCell && _.isNumber(cellValue) && cellValue === 0 && (
                <Box mr="8px">
                  <Icon icon="circle-check" color={colors.success} />
                </Box>
              )}

              {/* if there are more than 0 failed covenants */}
              {addIconToCell && _.isNumber(cellValue) && cellValue !== 0 && (
                <Box mr="8px">
                  <Icon icon="circle-x" color={colors.error} />
                </Box>
              )}

              <Box
                mr="8px"
                color={
                  fixedColumnsStyleToColumnIndexMap[index] && colors.blue[400]
                }
              >
                {flexRender(formattedCellValue, cell.getContext())}
              </Box>
            </Flex>
          </PortfolioOverviewFacilitiesTableTd>
        );
      })}
    </Tr>
  );
};

const PortfolioOverviewFacilitiesTableRowWithBorrowingBase: React.FC<{
  row: Row<PortfolioOverviewFacilityRow>;
  creditFacilityId: string;
}> = ({ row, creditFacilityId }) => {
  const {
    data: borrowingBaseInsightsData,
    loading: isBorrowingBaseInsightsLoading,
  } = useGetDailyCoreBorrowingBaseInsightsQuery({
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
  });

  if (!borrowingBaseInsightsData || isBorrowingBaseInsightsLoading) {
    return (
      <PortfolioOverviewFacilitiesTableRowSkeleton
        row={row}
        creditFacilityId={creditFacilityId}
      />
    );
  }

  const borrowingBaseInsightsForLatestReportingDate = _.first(
    borrowingBaseInsightsData.getDailyCoreBorrowingBaseInsights,
  );

  return (
    <PortfolioOverviewFacilitiesTableRow
      row={row}
      creditFacilityId={creditFacilityId}
      latestBorrowingBaseInsights={borrowingBaseInsightsForLatestReportingDate}
    />
  );
};

export const PortfolioOverviewFacilitiesTable: React.FC<{
  portfolioOverviewFacilityRows: PortfolioOverviewFacilityRow[];
}> = withLDConsumer()(({ flags, portfolioOverviewFacilityRows }) => {
  const initialColumnVisibility = createInitialColumnVisibility();
  const [data] = React.useState(() => portfolioOverviewFacilityRows);

  // tanstack table column state mgmt
  const [columnVisibility, setColumnVisibility] = React.useState(
    initialColumnVisibility,
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns: portfolioOverviewFacilitiesTableColumns,
    state: {
      columnVisibility,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  const { data: userData } = useGetUserActiveCreditFacilitiesQuery({
    variables: {
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });
  if (!userData || !userData.user) {
    return (
      <Skeleton data-testid="loading-verification-history" height="600px" />
    );
  }

  const {
    user: {
      organization: { creditFacilities },
    },
  } = userData;

  return (
    <Box>
      <PortfolioOverviewFacilitiesColumnSelector tableInstance={table} />
      <Box
        mt="32px"
        borderWidth="medium"
        borderColor={colors.gray[200]}
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        maxHeight="70vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
      >
        <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead>
            {_.map(table.getHeaderGroups(), (headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const columnCanSort = header.column.getCanSort();
                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      position="sticky"
                      top="0"
                      borderBottomWidth="thin"
                      zIndex={stickyHeaderRowZIndex}
                      style={fixedColumnsStyleToColumnIndexMap[index]}
                      whiteSpace="nowrap"
                    >
                      {header.isPlaceholder ? null : (
                        <Flex alignItems="center">
                          <Box
                            cursor={columnCanSort ? 'pointer' : 'default'}
                            onClick={header.column.getToggleSortingHandler()}
                            mr="8px"
                          >
                            {{
                              asc: <Icon icon="sort-up" />,
                              desc: <Icon icon="sort-down" />,
                            }[header.column.getIsSorted() as string] ??
                              (columnCanSort ? <Icon icon="sort" /> : null)}
                          </Box>

                          <Box mr="8px">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </Box>
                        </Flex>
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {_.map(table.getRowModel().rows, (row) => {
              const creditFacilitySlug: string = row.getValue(
                'creditFacilityName',
              );
              const creditFacilityId = getCreditFacilityIdForSlug(
                creditFacilitySlug,
                creditFacilities,
              );

              return (
                <PortfolioOverviewFacilitiesTableRowWithBorrowingBase
                  key={`portfolio-overview-facilities-row-${creditFacilityId}`}
                  row={row}
                  creditFacilityId={creditFacilityId}
                />
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
});
